import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Settings2, Save, Calculator } from "lucide-react";

export default function Shipping() {
  const [contacts, setContacts] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [history, setHistory] = useState([]);
  const [company, setCompany] = useState(null);
  const [tariffOpen, setTariffOpen] = useState(false);

  const load = useCallback(async () => {
    const [{ data: co }, { data: tr }, { data: hi }, { data: cp }] = await Promise.all([
      api.get("/contacts"),
      api.get("/vehicle-tariffs"),
      api.get("/shipping-calcs"),
      api.get("/settings/company"),
    ]);
    setContacts(co); setTariffs(tr); setHistory(hi); setCompany(cp);
  }, []);
  useEffect(() => { load(); }, [load]);

  const defaultTariff = useMemo(() => tariffs.find((t) => t.is_default) || tariffs[0] || null, [tariffs]);
  const factoryAddr = company?.address || "";

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Fiyat Hesapla · Nakliye</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Nakliye Maliyeti</h1>
          <p className="text-xs text-neutral-500 mt-1.5 max-w-xl">
            Yükleme ve teslim noktaları arasındaki mesafeye göre yaklaşık nakliye maliyeti hesaplayın.
            Hesaplar cari + tarih bilgisiyle kaydedilir.
          </p>
        </div>
        <button onClick={() => setTariffOpen(true)} data-testid="ship-tariffs-btn"
                className="brutal-btn bg-white text-black border border-black px-3 py-2.5 text-sm flex items-center gap-2">
          <Settings2 size={14} /> Araç Tarifeleri ({tariffs.length})
        </button>
      </header>

      <CalcForm
        contacts={contacts}
        tariffs={tariffs}
        defaultTariff={defaultTariff}
        factoryAddr={factoryAddr}
        onSaved={load}
      />

      <HistoryTable history={history} contacts={contacts} onDelete={async (id) => {
        if (!window.confirm("Bu hesap silinsin mi?")) return;
        await api.delete(`/shipping-calcs/${id}`);
        toast.success("Silindi"); load();
      }} />

      {tariffOpen && (
        <TariffsModal tariffs={tariffs} onClose={() => setTariffOpen(false)} onChanged={load} />
      )}
    </div>
  );
}

function CalcForm({ contacts, tariffs, defaultTariff, factoryAddr, onSaved }) {
  const [form, setForm] = useState({
    date: todayISO(), contact_id: "",
    from_location: factoryAddr || "Kartal / İstanbul",
    to_location: "",
    distance_km: "",
    vehicle_tariff_id: defaultTariff?.id || "",
    return_trip: false,
    include_fuel: false, fuel_price: "",
    loading_fee: "", labor_fee: "",
    box_count: "", notes: "",
  });

  useEffect(() => {
    if (factoryAddr && !form.from_location) setForm((f) => ({ ...f, from_location: factoryAddr }));
    if (defaultTariff && !form.vehicle_tariff_id) setForm((f) => ({ ...f, vehicle_tariff_id: defaultTariff.id }));
  }, [factoryAddr, defaultTariff]);  // eslint-disable-line

  const tariff = tariffs.find((t) => t.id === form.vehicle_tariff_id) || null;

  const preview = useMemo(() => {
    const distance = parseFloat(form.distance_km) || 0;
    const km_rate = tariff?.km_rate || 0;
    const eff = distance * (form.return_trip ? 2 : 1);
    const km_cost = eff * km_rate;
    let fuel_cost = 0;
    if (form.include_fuel && tariff?.fuel_lt_per_100km) {
      fuel_cost = eff * tariff.fuel_lt_per_100km / 100 * (parseFloat(form.fuel_price) || 0);
    }
    const loading = parseFloat(form.loading_fee) || 0;
    const labor = parseFloat(form.labor_fee) || 0;
    const total = km_cost + fuel_cost + loading + labor;
    const box = parseInt(form.box_count, 10);
    const per_unit = box && box > 0 ? total / box : null;
    return { eff, km_cost, fuel_cost, loading, labor, total, per_unit };
  }, [form, tariff]);

  const save = async () => {
    if (!form.from_location || !form.to_location) { toast.error("Yükleme ve teslim noktaları zorunlu"); return; }
    const distance = parseFloat(form.distance_km);
    if (isNaN(distance) || distance <= 0) { toast.error("Mesafe girin"); return; }
    if (!form.vehicle_tariff_id) { toast.error("Araç seçin (önce tarife tanımlayın)"); return; }
    try {
      const payload = {
        ...form,
        distance_km: distance,
        loading_fee: parseFloat(form.loading_fee) || 0,
        labor_fee: parseFloat(form.labor_fee) || 0,
        fuel_price: form.include_fuel ? (parseFloat(form.fuel_price) || 0) : null,
        box_count: form.box_count ? parseInt(form.box_count, 10) : null,
        contact_id: form.contact_id || null,
      };
      await api.post("/shipping-calcs", payload);
      toast.success("Hesap kaydedildi");
      setForm({ ...form, to_location: "", distance_km: "", box_count: "", notes: "" });
      onSaved();
    } catch (e) {
      toast.error("Kaydedilemedi: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  return (
    <div className="brutal-card p-5 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="ship-calc-form">
      <div className="lg:col-span-2 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Tarih">
            <input type="date" data-testid="ship-date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   className="input font-mono-num" />
          </FLabel>
          <FLabel label="Cari (opsiyonel)">
            <select data-testid="ship-contact" value={form.contact_id}
                    onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="input">
              <option value="">— Cari seçin —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FLabel>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Yükleme Noktası">
            <input data-testid="ship-from" value={form.from_location}
                   onChange={(e) => setForm({ ...form, from_location: e.target.value })}
                   className="input" placeholder="Kartal / İstanbul" />
          </FLabel>
          <FLabel label="Teslim Noktası">
            <input data-testid="ship-to" value={form.to_location}
                   onChange={(e) => setForm({ ...form, to_location: e.target.value })}
                   className="input" placeholder="Bursa / Nilüfer" />
          </FLabel>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FLabel label="Mesafe (km)">
            <input type="number" step="0.1" data-testid="ship-distance" value={form.distance_km}
                   onChange={(e) => setForm({ ...form, distance_km: e.target.value })}
                   className="input font-mono-num" />
          </FLabel>
          <FLabel label="Araç">
            <select data-testid="ship-vehicle" value={form.vehicle_tariff_id}
                    onChange={(e) => setForm({ ...form, vehicle_tariff_id: e.target.value })} className="input">
              {tariffs.length === 0 && <option value="">— Önce tarife tanımlayın —</option>}
              {tariffs.map((t) => <option key={t.id} value={t.id}>{t.name} · {fmtTRY(t.km_rate)}/km</option>)}
            </select>
          </FLabel>
          <FLabel label="Kutu Adedi">
            <input type="number" data-testid="ship-boxes" value={form.box_count}
                   onChange={(e) => setForm({ ...form, box_count: e.target.value })}
                   className="input font-mono-num" placeholder="Birim maliyeti için" />
          </FLabel>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Sabit Yükleme Ücreti">
            <input type="number" step="0.01" data-testid="ship-loading" value={form.loading_fee}
                   onChange={(e) => setForm({ ...form, loading_fee: e.target.value })}
                   className="input font-mono-num" placeholder="₺" />
          </FLabel>
          <FLabel label="Sürücü/İşçilik Ücreti">
            <input type="number" step="0.01" data-testid="ship-labor" value={form.labor_fee}
                   onChange={(e) => setForm({ ...form, labor_fee: e.target.value })}
                   className="input font-mono-num" placeholder="₺" />
          </FLabel>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="ship-return" checked={form.return_trip}
                   onChange={(e) => setForm({ ...form, return_trip: e.target.checked })}
                   className="w-4 h-4 accent-black" />
            İade dönüşü var (×2 mesafe)
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="ship-fuel-toggle" checked={form.include_fuel}
                   onChange={(e) => setForm({ ...form, include_fuel: e.target.checked })}
                   className="w-4 h-4 accent-black" />
            Yakıt ayrıca hesaplansın
          </label>
          {form.include_fuel && (
            <label className="inline-flex items-center gap-2">
              <span className="text-xs text-neutral-500">Yakıt (₺/L):</span>
              <input type="number" step="0.01" data-testid="ship-fuel-price" value={form.fuel_price}
                     onChange={(e) => setForm({ ...form, fuel_price: e.target.value })}
                     className="border border-black/20 px-2 py-1 w-20 text-xs font-mono-num" />
            </label>
          )}
        </div>
        <FLabel label="Not">
          <input data-testid="ship-notes" value={form.notes}
                 onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
        </FLabel>
      </div>

      <aside className="border-2 border-black p-4 bg-neutral-50 space-y-3">
        <div className="flex items-center gap-2 label-caps"><Calculator size={12} /> Ön İzleme</div>
        <PreviewRow label="Efektif mesafe" value={`${preview.eff.toLocaleString("tr-TR", { minimumFractionDigits: 1 })} km`} />
        <PreviewRow label="Km × Araç" value={fmtTRY(preview.km_cost)} />
        {form.include_fuel && <PreviewRow label="Yakıt maliyeti" value={fmtTRY(preview.fuel_cost)} />}
        <PreviewRow label="Yükleme" value={fmtTRY(preview.loading)} />
        <PreviewRow label="İşçilik" value={fmtTRY(preview.labor)} />
        <div className="border-t-2 border-black pt-2 mt-2">
          <PreviewRow label="TOPLAM" value={fmtTRY(preview.total)} big />
          {preview.per_unit != null && (
            <PreviewRow label="Kutu Başı" value={fmtTRY(preview.per_unit)} sub />
          )}
        </div>
        <button onClick={save} disabled={preview.total <= 0} data-testid="ship-save"
                className="brutal-btn bg-black text-white w-full py-2.5 text-sm mt-2 flex items-center justify-center gap-2 disabled:opacity-40">
          <Save size={14} /> Geçmişe Kaydet
        </button>
      </aside>
      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.7rem;outline:none;font-size:0.85rem}
                  .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
    </div>
  );
}

function PreviewRow({ label, value, big, sub }) {
  return (
    <div className={`flex items-center justify-between ${sub ? "text-xs mt-0.5" : ""}`}>
      <span className={`${big ? "font-display font-bold" : "text-xs text-neutral-600"}`}>{label}</span>
      <span className={`font-mono-num ${big ? "text-lg font-bold" : sub ? "text-neutral-500" : ""}`}>{value}</span>
    </div>
  );
}

function FLabel({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function HistoryTable({ history, contacts, onDelete }) {
  const cname = (id) => contacts.find((c) => c.id === id)?.name || "—";
  return (
    <div className="brutal-card overflow-hidden">
      <table className="w-full text-sm" data-testid="ship-history">
        <thead>
          <tr className="border-b-2 border-black/80">
            <th className="text-left px-4 py-3 label-caps w-28">Tarih</th>
            <th className="text-left px-4 py-3 label-caps">Cari</th>
            <th className="text-left px-4 py-3 label-caps">Güzergâh</th>
            <th className="text-right px-4 py-3 label-caps w-24">Mesafe</th>
            <th className="text-left px-4 py-3 label-caps">Araç</th>
            <th className="text-right px-4 py-3 label-caps">Kutu Başı</th>
            <th className="text-right px-4 py-3 label-caps">Toplam</th>
            <th className="px-4 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {history.length === 0 ? (
            <tr><td colSpan={8} className="text-center py-8 text-neutral-500 text-sm">Kayıt yok.</td></tr>
          ) : history.map((h) => (
            <tr key={h.id} className="border-b border-black/5 hover:bg-neutral-50">
              <td className="px-4 py-2.5 font-mono-num">{fmtDate(h.date)}</td>
              <td className="px-4 py-2.5 text-neutral-700">{cname(h.contact_id)}</td>
              <td className="px-4 py-2.5 text-xs">
                <div className="text-neutral-500">{h.from_location}</div>
                <div className="text-neutral-500">→ {h.to_location}</div>
              </td>
              <td className="px-4 py-2.5 text-right font-mono-num">{h.distance_km} km{h.return_trip ? " ×2" : ""}</td>
              <td className="px-4 py-2.5 text-xs">{h.vehicle_name || "—"}</td>
              <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">
                {h.per_unit_cost != null ? fmtTRY(h.per_unit_cost) : "—"}
              </td>
              <td className="px-4 py-2.5 text-right font-mono-num font-bold text-rose-600">{fmtTRY(h.total_cost)}</td>
              <td className="px-4 py-2.5 text-right">
                <button onClick={() => onDelete(h.id)} data-testid={`ship-del-${h.id}`}
                        className="p-1 hover:bg-rose-50"><Trash2 size={13} className="text-rose-600" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TariffsModal({ tariffs, onClose, onChanged }) {
  const [form, setForm] = useState({ name: "", km_rate: "", fuel_lt_per_100km: "", is_default: false });
  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Araç adı"); return; }
    try {
      await api.post("/vehicle-tariffs", {
        name: form.name.trim(),
        km_rate: parseFloat(form.km_rate) || 0,
        fuel_lt_per_100km: form.fuel_lt_per_100km ? parseFloat(form.fuel_lt_per_100km) : null,
        is_default: form.is_default,
      });
      setForm({ name: "", km_rate: "", fuel_lt_per_100km: "", is_default: false });
      onChanged();
      toast.success("Eklendi");
    } catch (err) { toast.error("Eklenemedi: " + (err?.response?.data?.detail || err?.message || "")); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`${name} silinsin mi?`)) return;
    await api.delete(`/vehicle-tariffs/${id}`);
    toast.success("Silindi"); onChanged();
  };
  const setDefault = async (t) => {
    await api.put(`/vehicle-tariffs/${t.id}`, { is_default: true });
    onChanged();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           data-testid="ship-tariffs-modal"
           className="bg-white border border-black w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Truck size={16} /> Araç Tarifeleri</h2>
          <button onClick={onClose} className="p-1 text-xl leading-none">×</button>
        </div>
        <form onSubmit={add} className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3 border-b border-black/10 bg-neutral-50">
          <label className="text-xs">
            <div className="label-caps mb-1">Araç adı</div>
            <input required data-testid="tariff-name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Küçük Kamyonet"
                   className="border border-black/20 px-2 py-1.5 w-full text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-caps mb-1">Km birim (₺)</div>
            <input required type="number" step="0.01" data-testid="tariff-rate" value={form.km_rate}
                   onChange={(e) => setForm({ ...form, km_rate: e.target.value })}
                   className="border border-black/20 px-2 py-1.5 w-full text-sm font-mono-num" />
          </label>
          <label className="text-xs">
            <div className="label-caps mb-1">Yakıt (L/100km)</div>
            <input type="number" step="0.1" data-testid="tariff-fuel" value={form.fuel_lt_per_100km}
                   onChange={(e) => setForm({ ...form, fuel_lt_per_100km: e.target.value })}
                   className="border border-black/20 px-2 py-1.5 w-full text-sm font-mono-num" />
          </label>
          <label className="text-xs flex items-end gap-1.5 pb-1">
            <input type="checkbox" checked={form.is_default}
                   onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                   className="w-4 h-4 accent-black" />
            Varsayılan
            <button type="submit" data-testid="tariff-add"
                    className="ml-auto brutal-btn bg-black text-white px-3 py-1.5 text-xs">Ekle</button>
          </label>
        </form>
        <div className="p-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-black/80">
                <th className="text-left py-2 label-caps">Araç</th>
                <th className="text-right py-2 label-caps">Km (₺)</th>
                <th className="text-right py-2 label-caps">Yakıt (L/100)</th>
                <th className="text-center py-2 label-caps w-24">Varsayılan</th>
                <th className="py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {tariffs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-4 text-neutral-500">Tarife yok.</td></tr>
              ) : tariffs.map((t) => (
                <tr key={t.id} className="border-b border-black/5">
                  <td className="py-2">{t.name}</td>
                  <td className="py-2 text-right font-mono-num">{fmtTRY(t.km_rate)}</td>
                  <td className="py-2 text-right font-mono-num">{t.fuel_lt_per_100km ?? "—"}</td>
                  <td className="py-2 text-center">
                    {t.is_default ? (
                      <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border border-emerald-500 bg-emerald-50 text-emerald-700">Varsayılan</span>
                    ) : (
                      <button onClick={() => setDefault(t)} className="text-xs underline text-neutral-500">Yap</button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => remove(t.id, t.name)} className="p-1 hover:bg-rose-50">
                      <Trash2 size={13} className="text-rose-600" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
